import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTeam, inviteMember, revokeInvite, resendInvite, updateMemberRole, removeMember, apiError,
} from "@/lib/api";
import { PageHeader, Card, Loading, ConfirmDelete } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Users, Crown, Mail, Trash2, RotateCcw, ShieldCheck, Sparkles } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin", desc: "Manage operations, team & most settings" },
  { value: "manager", label: "Manager", desc: "Create & edit documents, record payments" },
  { value: "staff", label: "Staff", desc: "Create & edit permitted documents" },
  { value: "viewer", label: "Viewer", desc: "Read-only access" },
];

const ROLE_BADGE = {
  owner: "bg-[#D4AF37]/15 text-[#8a6d0f] border-[#D4AF37]/40",
  admin: "bg-[#1E7A6B]/12 text-[#0f5346] border-[#1E7A6B]/30",
  manager: "bg-indigo-50 text-indigo-700 border-indigo-200",
  staff: "bg-slate-100 text-slate-700 border-slate-200",
  viewer: "bg-amber-50 text-amber-700 border-amber-200",
};

function RoleBadge({ role }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${ROLE_BADGE[role] || ROLE_BADGE.staff}`} data-testid={`role-badge-${role}`}>
      {role === "owner" && <Crown className="w-3 h-3" />}
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </span>
  );
}

export default function Team() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("staff");
  const [busy, setBusy] = useState(false);

  const load = () => getTeam().then(setData).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  if (!data) return <Loading />;

  const atLimit = data.remaining <= 0;
  const canInvite = data.can_invite;

  const submitInvite = async () => {
    if (!email.trim()) return toast.error("Enter an email address");
    setBusy(true);
    try {
      await inviteMember(email.trim(), role);
      toast.success("Invitation sent");
      setInviteOpen(false); setEmail(""); setRole("staff");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const changeRole = async (uid, newRole) => {
    try { await updateMemberRole(uid, newRole); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doRemove = async (uid) => {
    try { await removeMember(uid); toast.success("Member removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doRevoke = async (id) => {
    try { await revokeInvite(id); toast.success("Invitation revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doResend = async (id) => {
    try { await resendInvite(id); toast.success("Invitation resent"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="team-page">
      <PageHeader title="Team Management" subtitle="Invite teammates and control what they can access">
        {canInvite && (
          <Button
            onClick={() => setInviteOpen(true)}
            disabled={atLimit}
            className="bg-[#1E7A6B] hover:bg-[#186052] text-white"
            data-testid="invite-member-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Invite Team Member
          </Button>
        )}
      </PageHeader>

      {/* Plan capacity */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><ShieldCheck className="w-4 h-4" /> Current Plan</div>
          <div className="mt-2 text-2xl font-bold text-[#0B132B]" data-testid="team-plan">{data.plan_name}</div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Users className="w-4 h-4" /> Team Members</div>
          <div className="mt-2 text-2xl font-bold text-[#0B132B]" data-testid="team-usage">{data.used} / {data.limit}</div>
          <div className="mt-2 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${Math.min((data.used / data.limit) * 100, 100)}%` }} />
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Sparkles className="w-4 h-4" /> API Access</div>
          <div className="mt-2 text-2xl font-bold text-[#0B132B]">{data.api_enabled ? "Enabled" : "Pro only"}</div>
          {!data.api_enabled && (
            <button onClick={() => nav("/billing")} className="mt-1 text-xs text-[#1E7A6B] font-semibold hover:underline" data-testid="team-upgrade-api">Upgrade to Pro →</button>
          )}
        </Card>
      </div>

      {atLimit ? (
        <div className="mb-5 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="team-limit-banner">
          Team member limit reached. {data.plan === "business" ? "Upgrade to Pro for up to 5 team members." : data.plan === "pro" ? "" : "Upgrade your plan to add more team members."}
        </div>
      ) : (
        <div className="mb-5 text-sm text-slate-500" data-testid="team-remaining">
          {data.remaining} team member slot{data.remaining === 1 ? "" : "s"} remaining.
        </div>
      )}

      {/* Members table */}
      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 font-semibold text-[#0B132B]">Members</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-members-table">
            <thead>
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wide border-b border-slate-100">
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Email</th>
                <th className="px-5 py-3 font-semibold">Role</th>
                <th className="px-5 py-3 font-semibold">Joined</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id} className="border-b border-slate-50 last:border-0" data-testid={`member-row-${m.id}`}>
                  <td className="px-5 py-4 font-medium text-[#0B132B]">{m.name || "—"}{m.id === user?.id && <span className="ml-2 text-xs text-slate-400">(You)</span>}</td>
                  <td className="px-5 py-4 text-slate-600">{m.email}</td>
                  <td className="px-5 py-4">
                    {m.is_owner ? <RoleBadge role="owner" /> : (
                      user?.permissions?.includes("team.edit") || user?.is_owner ? (
                        <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                          <SelectTrigger className="w-[130px] h-8" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : <RoleBadge role={m.role} />
                    )}
                  </td>
                  <td className="px-5 py-4 text-slate-500">{m.joined_at ? fmtDate(m.joined_at) : "—"}</td>
                  <td className="px-5 py-4 text-right">
                    {m.is_owner ? <span className="text-xs text-slate-300">—</span> : (
                      (user?.permissions?.includes("team.remove") || user?.is_owner) && m.id !== user?.id ? (
                        <ConfirmDelete
                          onConfirm={() => doRemove(m.id)}
                          title="Remove team member?"
                          description={`${m.email} will lose access to this workspace.`}
                          testId={`remove-member-dialog-${m.id}`}
                          trigger={<Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`remove-member-${m.id}`}><Trash2 className="w-4 h-4" /></Button>}
                        />
                      ) : <span className="text-xs text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pending invitations */}
      {data.invitations?.length > 0 && (
        <Card className="overflow-hidden mt-6">
          <div className="px-5 py-4 border-b border-slate-100 font-semibold text-[#0B132B]">Pending Invitations</div>
          <div className="divide-y divide-slate-50">
            {data.invitations.map((inv) => (
              <div key={inv.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`invite-row-${inv.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><Mail className="w-4 h-4" /></div>
                  <div>
                    <div className="font-medium text-[#0B132B]">{inv.email}</div>
                    <div className="text-xs text-slate-400">Invited {fmtDate(inv.created_at)} · expires {fmtDate(inv.expires_at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <RoleBadge role={inv.role} />
                  {canInvite && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => doResend(inv.id)} data-testid={`resend-invite-${inv.id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Resend</Button>
                      <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={() => doRevoke(inv.id)} data-testid={`revoke-invite-${inv.id}`}>Revoke</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>They'll receive a secure email link to set a password and join your workspace.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="invite-email">Email address</Label>
              <Input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" data-testid="invite-email-input" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      <div><div className="font-medium">{r.label}</div><div className="text-xs text-slate-400">{r.desc}</div></div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={submitInvite} disabled={busy} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="send-invite-btn">
              {busy ? "Sending…" : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
